import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../hooks/usePublicSettings';

interface LoginSignupModalProps {
  open: boolean;
  onClose: () => void;
}

const LoginSignupModal: React.FC<LoginSignupModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { handleLoginClick } = useAuth();
  const { companyName } = usePublicSettings();

  const handleLogin = () => {
    // Store current URL for redirect after login
    window.localStorage.setItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL', window.location.href);
    handleLoginClick();
    onClose();
  };

  const handleSignup = () => {
    // Store current URL for redirect after signup
    const currentUrl = window.location.href;
    navigate('/signup', { state: { returnUrl: currentUrl } });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 2,
          pt: 3,
          fontSize: '1.5rem',
          fontWeight: 700,
        }}
      >
        Login or Sign Up
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Explanation Section */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#E3F2FD',
              borderRadius: '8px',
              border: '1px solid #BBDEFB',
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#1976D2',
                lineHeight: 1.6,
                textAlign: 'center',
                fontSize: '0.9rem',
              }}
            >
              {companyName || 'We'} use Kindertales to support this registration process.
            </Typography>
          </Box>

          {/* Existing User Section */}
          <Box
            sx={{
              p: 2.5,
              backgroundColor: '#F5F5F5',
              borderRadius: '8px',
              border: '1px solid #E0E0E0',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                fontSize: '1.1rem',
              }}
            >
              Already a Kindertales user?
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              Sign in with your existing Kindertales account to continue with your purchase.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={handleLogin}
              sx={{
                backgroundColor: '#297AB0',
                color: 'white',
                py: 1.25,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#1f5d85',
                },
              }}
            >
              Sign In
            </Button>
          </Box>

          <Divider sx={{ my: 1 }}>
            <Typography variant="body2" sx={{ color: '#999', px: 2 }}>
              OR
            </Typography>
          </Divider>

          {/* New User Section */}
          <Box
            sx={{
              p: 2.5,
              backgroundColor: '#F5F5F5',
              borderRadius: '8px',
              border: '1px solid #E0E0E0',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                fontSize: '1.1rem',
              }}
            >
              New to Kindertales?
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              Create a new account to get started. You'll be able to register for events and manage your children's activities.
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleSignup}
              sx={{
                borderColor: '#297AB0',
                color: '#297AB0',
                py: 1.25,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                  borderColor: '#1f5d85',
                  backgroundColor: 'rgba(41, 122, 176, 0.04)',
                },
              }}
            >
              Sign Up
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1.5 }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#666',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Typography
          variant="caption"
          sx={{
            color: '#999',
            fontSize: '0.75rem',
            textAlign: 'center',
          }}
        >
          {' '}
          <Box
            component="a"
            href="https://www.kindertales.com/terms-of-service/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#297AB0',
              textDecoration: 'underline',
              '&:hover': {
                color: '#1f5d85',
              },
            }}
          >
            Kindertales Terms of Service
          </Box>
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default LoginSignupModal;

